import { DateFormats, formatDate } from '@strata/helper-functions';
import { FormPageContainer } from '@strata/react-form-header-and-content';
import { GridWithFilters, SecondaryData } from '@strata/react-grid';
import { SuggestSingleSelect } from '@strata/react-selects';
import { StatusIndicator } from '@strata/react-status-indicators';
import { useLocation, useNavigate, useParams } from 'react-router';

import { TreatmentMergeCaseStatus, TreatmentMergeCaseStatusIndicatorMap } from 'services/constants';
import { AppRoutes } from 'services/routes';
import { useGetPayersQuery } from 'store/api/referenceApi';
import { useGetTreatmentMergeCasesQuery } from 'store/api/treatmentApi';
import { Grids, useGridState } from 'store/grids/gridsSlice';

const initialSortProperties = [{ id: 'createdAt', desc: false }];

const defaultStatusFilters = [
    TreatmentMergeCaseStatus.PENDING,
    TreatmentMergeCaseStatus.SPLIT_REQUIRED,
];

const columns = [
    {
        accessorKey: 'createdAt',
        header: 'Opened',
        size: 120,
        cell: ({ getValue }) => (
            <SecondaryData data={formatDate(getValue(), DateFormats.DATE_TIME)} />
        ),
    },
    { accessorKey: 'productServiceOid', header: 'Product', size: 200 },
    { accessorKey: 'treatmentCount', header: 'Treatments', size: 100 },
    { accessorKey: 'fmhPatientId', header: 'FMH Patient', size: 280 },
    {
        accessorKey: 'status',
        header: 'Status',
        size: 120,
        cell: ({ getValue }) => (
            <StatusIndicator
                status={
                    TreatmentMergeCaseStatusIndicatorMap[getValue()]?.status ??
                    StatusIndicator.Status.DEFAULT
                }
                message={TreatmentMergeCaseStatusIndicatorMap[getValue()]?.message ?? getValue()}
            />
        ),
    },
    { accessorKey: 'resolvedBy', header: 'Resolved By', size: 200 },
];

const filterCategories = [
    {
        key: 'status',
        title: 'Status',
        showCounts: true,
        valueDisplayTextMap: Object.fromEntries(
            Object.entries(TreatmentMergeCaseStatusIndicatorMap).map(([status, { message }]) => [
                status,
                message,
            ]),
        ),
        defaultValue: defaultStatusFilters,
    },
    { key: 'productServiceOid', title: 'Product', showCounts: true },
];

export const TreatmentMergeCasesListPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { payerId } = useParams();

    const {
        activeFilters,
        filterPaneWidth,
        search,
        sort,
        updateFilterPaneWidth,
        updateGridActiveFilters,
        updateGridSearch,
        updateGridSort,
    } = useGridState(Grids.TREATMENT_MERGE_CASES, initialSortProperties);

    const { data: payers } = useGetPayersQuery();
    const { data: mergeCases, isFetching } = useGetTreatmentMergeCasesQuery(
        { payerId },
        { skip: !payerId },
    );

    const gridData =
        mergeCases?.map((mergeCase) => ({
            ...mergeCase,
            treatmentCount: mergeCase.treatmentIds.length,
        })) ?? [];
    const backToDestination = {
        route: `${location.pathname}${location.search}`,
        text: 'Treatment Merge Queue',
    };

    return (
        <FormPageContainer>
            <GridWithFilters
                title="Treatment Merge Queue"
                activeFilters={activeFilters}
                columns={columns}
                data={gridData}
                isLoading={isFetching}
                sortProperties={sort}
                onSort={updateGridSort}
                enableSearch
                searchText={search}
                onSearch={updateGridSearch}
                filterCategories={filterCategories}
                onFilter={updateGridActiveFilters}
                filterPaneRenderWidth={filterPaneWidth}
                onFilterPaneResize={updateFilterPaneWidth}
                onRowClick={(mergeCase) =>
                    navigate(AppRoutes.treatmentMergeCaseDetails(payerId, mergeCase.id), {
                        state: { backToDestination },
                    })
                }
                topRightContent={
                    <SuggestSingleSelect
                        id="payerId"
                        name="payerId"
                        placeholderText="Select a payer"
                        values={payers?.payerList}
                        selectedItem={payers?.payerMap?.[payerId] ?? null}
                        isLoading={!payers}
                        onChange={(payer) =>
                            navigate(
                                payer
                                    ? AppRoutes.payerTreatmentMergeCases(payer.payer_id)
                                    : AppRoutes.treatmentMergeCases,
                            )
                        }
                    />
                }
            />
        </FormPageContainer>
    );
};
